import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  useDocumentationLoader,
  categories,
  cleanMarkdownForDisplay
} from '../utils/documentationLoader';
import './DocumentationPage.css';
import PdfViewer from '../components/PdfViewer/PdfViewer';

const DocumentationPage: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>(section || 'faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Load documentation from markdown files
  const { docSections, loading, error } = useDocumentationLoader();

  // Update active section when URL parameter changes
  useEffect(() => {
    if (section && section !== activeSection) {
      setActiveSection(section);
    }
  }, [section, activeSection]);

  // Navigate to section
  const navigateToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    navigate(`/docs/${sectionId}`);
    setIsSidebarOpen(false);
  };

  // Filter sections based on search query
  const filteredSections = docSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get active content
  const activeContent = docSections.find(section => section.id === activeSection)?.content || '';
  const cleanedContent = cleanMarkdownForDisplay(activeContent);

  // Loading state
  if (loading) {
    return (
      <div className="documentation-page dark">
        <div className="docs-loading">
          <div className="loading-spinner"></div>
          <p>Loading documentation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="documentation-page dark">
        <div className="docs-error">
          <h2>Error Loading Documentation</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="documentation-page dark">
      <div className="docs-container">
        {/* Sidebar */}
        <aside className={`docs-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          {/* Search */}
          <div className="docs-search">
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>


          {/* Navigation */}
          <nav className="docs-nav">
            {categories.map(category => {
              const categorySections = filteredSections.filter(section => section.category === category);
              if (categorySections.length === 0) return null;

              return (
                <div key={category} className="nav-category">
                  <h3 className="nav-category-title">{category}</h3>
                  <ul className="nav-section-list">
                    {categorySections.map(section => (
                      <li key={section.id}>
                        <button
                          className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                          onClick={() => navigateToSection(section.id)}
                          title={section.fileName}
                        >
                          {section.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>

          {/* Documentation Statistics */}
          <div className="docs-stats">
            <h4>Categories</h4>
            {categories.map(category => {
              const count = docSections.filter(doc => doc.category === category).length;
              return (
                <div key={category} className="category-stat">
                  <span className="category-name">{category}</span>
                  <span className="category-count">{count}</span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main Content */}
        <main className="docs-main">
          <div className="docs-content">
            {activeContent ? (
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="docs-article"
              >
                <div className="article-header">
                  <h1>{docSections.find(s => s.id === activeSection)?.title}</h1>
                  <div className="article-meta">
                    <span className="category-badge">
                      {docSections.find(s => s.id === activeSection)?.category}
                    </span>
                    <span className="file-name">
                      {docSections.find(s => s.id === activeSection)?.fileName}
                    </span>
                  </div>
                </div>
                <div className="article-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      // Handle PDF viewer divs that we convert from object tags
                      div: (props: any) => {
                        // Check if this is our PDF viewer div
                        if (props['data-pdf-viewer'] === 'true') {
                          const src = props['data-pdf-src'] || '';
                          const title = props['data-pdf-title'] || 'Document';
                          const isAbsolute = /^https?:\/\//i.test(src);
                          const absUrl = isAbsolute ? src : `${window.location.origin}${src?.startsWith('/') ? '' : '/'}${src}`;
                          return (
                            <PdfViewer
                              src={absUrl}
                              title={title}
                              className="pdf-embed"
                              height="auto"
                            />
                          );
                        }
                        
                        return <div {...props} />;
                      },
                      // Custom rendering for code blocks
                      code: ({ className, children, ...props }) => {
                        const match = /language-(\w+)/.exec(className || '');
                        const isCodeBlock = className?.includes('language-');
                        return isCodeBlock ? (
                          <pre className={`code-block ${match ? `language-${match[1]}` : ''}`}>
                            <code className={className} {...props}>
                              {children}
                            </code>
                          </pre>
                        ) : (
                          <code className={`inline-code ${className || ''}`} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // Custom rendering for tables
                      table: ({ children }) => (
                        <div className="table-wrapper">
                          <table>{children}</table>
                        </div>
                      ),
                      // Custom rendering for links
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          target={href?.startsWith('http') ? '_blank' : '_self'}
                          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {children}
                        </a>
                      ),
                      // Fallback: Render PDFs via a self-hosted PDF.js viewer for any remaining object tags
                      object: (props: any) => {
                        const dataAttr: string = props?.data || props?.['data'] || '';
                        const title: string = props?.['aria-label'] || 'Document';
                        const isAbsolute = /^https?:\/\//i.test(dataAttr);
                        const absUrl = isAbsolute ? dataAttr : `${window.location.origin}${dataAttr?.startsWith('/') ? '' : '/'}${dataAttr || ''}`;
                        const isPdf = /\.pdf(?:[#?].*)?$/i.test(absUrl || '');
                        if (!isPdf) {
                          return <object {...props} aria-label="Document" />;
                        }
                        return (
                          <PdfViewer
                            src={absUrl}
                            title={title}
                            className="pdf-embed"
                            height="auto"
                          />
                        );
                      },
                      // Normalize any iframe (including legacy PDF iframes) to responsive sizing
                      iframe: (props: any) => (
                        <iframe
                          {...props}
                          title={props.title || 'Embedded content'}
                          className={`pdf-embed ${props.className || ''}`}
                          style={{ width: '100%', height: '75vh', border: 0, ...(props.style || {}) }}
                          referrerPolicy={props.referrerPolicy || 'no-referrer'}
                        />
                      ),
                      // Custom rendering for details/summary
                      details: ({ children }) => (
                        <details className="expandable-section">
                          {children}
                        </details>
                      ),
                      summary: ({ children }) => (
                        <summary className="expandable-title">
                          {children}
                        </summary>
                      )
                    }}
                  >
                    {cleanedContent}
                  </ReactMarkdown>
                </div>
              </motion.div>
            ) : (
              <div className="docs-not-found">
                <h2>Document Not Found</h2>
                <p>The requested documentation section could not be found.</p>
                <button onClick={() => navigateToSection('faq')}>
                  Go to FAQ
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DocumentationPage;